import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PlaceIcon from '@mui/icons-material/Place';
import FlagIcon from '@mui/icons-material/Flag';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Colors for different stop types
const stopColors = {
  start: '#1976d2', // Blue
  intermediate: '#ff9800', // Orange
  end: '#2e7d32', // Green
};

// Function to get icon based on stop type
const getStopIcon = (type) => {
  switch(type) {
    case 'start':
      return <DirectionsCarIcon />;
    case 'intermediate':
      return <LocationOnIcon />;
    case 'end':
      return <FlagIcon />;
    default:
      return <PlaceIcon />;
  }
};

// Function to get color based on stop type for MUI components
const getStopTypeColor = (type) => {
  switch(type) {
    case 'start':
      return 'primary';
    case 'intermediate':
      return 'warning';
    case 'end':
      return 'success';
    default:
      return 'default';
  }
};

// Calculate total route distance by summing all segments
const calculateTotalDistance = (stops) => {
  if (!stops || stops.length < 2) return 0;
  
  let total = 0;
  
  // Sum all distance_from_previous values
  for (let i = 1; i < stops.length; i++) {
    if (stops[i].distance_from_previous) {
      total += parseFloat(stops[i].distance_from_previous);
    }
  }
  
  return Math.round(total * 10) / 10; // Round to 1 decimal place
};

const TripMap = ({ trip, totalKilometers, actualKilometers }) => {
  const [selectedStop, setSelectedStop] = useState(null);
  const mapInitialized = useRef(false);
  
  // Default actualKilometers to calculated distance if not provided
  const displayActualKilometers = actualKilometers || calculateTotalDistance(trip) || 0;
  
  // Initialize the map when the component mounts or trip data changes
  useEffect(() => {
    // Only initialize once trip data is available
    if (trip && trip.length > 0 && !mapInitialized.current) {
      const iframe = document.getElementById('osm-map-iframe');
      if (iframe) {
        // Wait for iframe to load
        iframe.onload = () => {
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            
            // Add OpenStreetMap and Leaflet dependencies
            const leafletCss = document.createElement('link');
            leafletCss.rel = 'stylesheet';
            leafletCss.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            iframeDoc.head.appendChild(leafletCss);
            
            const leafletScript = document.createElement('script');
            leafletScript.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            iframeDoc.head.appendChild(leafletScript);
            
            // Add styles
            const style = document.createElement('style');
            style.textContent = `
              body, html { margin: 0; padding: 0; height: 100%; width: 100%; }
              #map { height: 100%; width: 100%; }
            `;
            iframeDoc.head.appendChild(style);
            
            // Create map container
            const mapDiv = document.createElement('div');
            mapDiv.id = 'map';
            iframeDoc.body.appendChild(mapDiv);
            
            // Initialize map after Leaflet loads
            leafletScript.onload = () => {
              // Mark as initialized
              mapInitialized.current = true;
              
              // Extract coordinates
              const coordinates = [];
              
              trip.forEach(stop => {
                const lat = parseFloat(stop.latitude);
                const lon = parseFloat(stop.longitude);
                if (!isNaN(lat) && !isNaN(lon)) {
                  coordinates.push({
                    type: stop.type || 'intermediate',
                    lat,
                    lon,
                    name: stop.name,
                    distance: stop.distance_from_previous
                  });
                }
              });
              
              if (coordinates.length > 0) {
                // Create map
                const L = iframe.contentWindow.L;
                const map = L.map('map');
                
                // Add OpenStreetMap tiles
                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                  maxZoom: 19,
                  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                }).addTo(map);
                
                // Create bounds
                const bounds = L.latLngBounds(coordinates.map(coord => [coord.lat, coord.lon]));
                map.fitBounds(bounds, { padding: [30, 30] });
                
                // Add markers for each stop
                coordinates.forEach((coord, index) => {
                  // Create custom marker with icon based on type
                  const markerHtmlStyles = `
                    width: 2.5rem;
                    height: 2.5rem;
                    display: block;
                    left: -1.25rem;
                    top: -1.25rem;
                    position: relative;
                    border-radius: 3rem 3rem 0;
                    transform: rotate(45deg);
                    border: 1px solid #FFFFFF;
                    background-color: ${
                      coord.type === 'start' ? '#1976d2' : 
                      coord.type === 'end' ? '#2e7d32' : 
                      '#ff9800'
                    };
                  `;
                  
                  const icon = L.divIcon({
                    className: "custom-pin",
                    iconAnchor: [0, 24],
                    labelAnchor: [-6, 0],
                    popupAnchor: [0, -36],
                    html: `<span style="${markerHtmlStyles}" />
                           <span style="position: relative; top: -45px; left: -10px; background: white; padding: 2px 6px; border-radius: 10px; font-weight: bold; font-size: 14px; color: black;">${index + 1}</span>`
                  });
                  
                  // Add marker to map
                  const marker = L.marker([coord.lat, coord.lon], { icon }).addTo(map);
                  
                  // Add popup with info
                  const popupContent = `
                    <div style="min-width: 150px;">
                      <h3 style="margin: 4px 0; font-size: 14px;">${index + 1}. ${coord.name || 'Unnamed stop'}</h3>
                      <p style="margin: 4px 0; font-size: 12px; color: #666;">
                        Type: ${coord.type}<br>
                        ${coord.distance ? `Distance: ${coord.distance} km` : ''}
                      </p>
                      <p style="margin: 4px 0; font-size: 10px; color: #999;">
                        ${coord.lat.toFixed(6)}, ${coord.lon.toFixed(6)}
                      </p>
                    </div>
                  `;
                  marker.bindPopup(popupContent);
                });
                
                // Draw polyline for the route
                const routeCoordinates = coordinates.map(coord => [coord.lat, coord.lon]);
                const routeLine = L.polyline(routeCoordinates, {
                  color: '#3388ff',
                  weight: 4,
                  opacity: 0.7,
                  lineJoin: 'round'
                }).addTo(map);
              }
            };
          } catch (error) {
            console.error("Error initializing map:", error);
          }
        };
        
        // Trigger load
        iframe.src = 'about:blank';
      }
    }
  }, [trip]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Trip Map
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        {/* Map visualization using OpenStreetMap iframe */}
        <Box sx={{ 
          width: { xs: '100%', md: '70%' }, 
          height: '500px', 
          borderRadius: '12px',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          {trip && trip.length > 0 ? (
            <>
              {/* Use OpenStreetMap with JavaScript framework for better marker handling */}
              <iframe 
                title="Trip Route Map"
                width="100%" 
                height="100%" 
                frameBorder="0" 
                scrolling="no" 
                marginHeight="0" 
                marginWidth="0" 
                src={`about:blank`}
                style={{ border: '1px solid #ddd', borderRadius: '12px' }}
                id="osm-map-iframe"
              />
              
              {/* Map will be initialized with useEffect */}
              
              {/* Note about cached distances */}
              <Box sx={{ 
                position: 'absolute', 
                bottom: 12, 
                left: 12,
                zIndex: 1000,
                bgcolor: 'rgba(255,255,255,0.8)',
                borderRadius: 1,
                p: 0.5
              }}>
                <Typography variant="caption" color="text.secondary">
                  Using cached distances only
                </Typography>
              </Box>
            </>
          ) : (
            <Box 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                bgcolor: '#f5f5f5'
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No trip data available
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Trip Details Panel */}
        <Paper 
          elevation={3} 
          sx={{ 
            width: { xs: '100%', md: '30%' }, 
            maxHeight: '500px',
            overflow: 'auto',
            borderRadius: '12px',
            backgroundColor: '#f9f9f9'
          }}
        >
          <List dense sx={{ p: 0 }}>
            <ListItem sx={{ bgcolor: '#f0f0f0', py: 1.5 }}>
              <ListItemText 
                primary={<Typography variant="subtitle1" fontWeight="bold">Trip Stops</Typography>} 
                secondary={
                  <Box>
                    <Typography variant="body2">{`Total: ${trip ? trip.length : 0} stops`}</Typography>
                    
                    {/* Calculate and display total distances */}
                    {trip && trip.length > 0 && (
                      <Box 
                        sx={{ 
                          mt: 1, 
                          p: 1, 
                          bgcolor: 'background.paper', 
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 'bold', 
                            display: 'block',
                            mb: 0.5,
                            color: 'text.secondary'
                          }}
                        >
                          Trip Distance Summary
                        </Typography>
                        
                        {/* Rental distance (target) */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          mb: 0.5
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                            Rental Distance:
                          </Typography>
                          <Chip 
                            size="small" 
                            variant="outlined" 
                            color="secondary"
                            label={`${totalKilometers || 0} km`}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                        
                        {/* Actual distance (calculated from stops) */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                            Actual Distance:
                          </Typography>
                          <Chip 
                            size="small" 
                            variant="outlined" 
                            color="primary"
                            label={`${displayActualKilometers} km`}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                        
                        {/* Difference percentage */}
                        {totalKilometers && (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            mt: 0.5,
                            borderTop: '1px dashed #e0e0e0',
                            pt: 0.5
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                              Difference:
                            </Typography>
                            
                            {(() => {
                              const actualDistance = displayActualKilometers;
                              const targetDistance = parseFloat(totalKilometers);
                              const diff = Math.abs(actualDistance - targetDistance);
                              const percentage = ((diff / targetDistance) * 100).toFixed(1);
                              const isNegative = actualDistance < targetDistance;
                              
                              // Use more flexible thresholds for larger trips
                              const isLargeTrip = targetDistance > 500;
                              const successThreshold = isLargeTrip ? 15 : 5;
                              const warningThreshold = isLargeTrip ? 30 : 10;
                              
                              return (
                                <Chip 
                                  size="small" 
                                  variant="outlined" 
                                  color={percentage <= successThreshold ? "success" : percentage <= warningThreshold ? "warning" : "error"}
                                  label={`${isNegative ? "-" : "+"}${percentage}%`}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              );
                            })()}
                          </Box>
                        )}
                        
                        {/* Number of stops */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          mt: 0.5,
                          borderTop: '1px solid #e0e0e0',
                          pt: 0.5
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                            Number of Stops:
                          </Typography>
                          <Chip 
                            size="small" 
                            variant="outlined" 
                            color="default"
                            label={trip.length} 
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                }
              />
            </ListItem>
            
            <Divider />
            
            {trip && trip.map((stop, index) => (
              <React.Fragment key={index}>
                <ListItem 
                  alignItems="flex-start" 
                  onClick={() => setSelectedStop(stop)}
                  selected={selectedStop && selectedStop.name === stop.name}
                  sx={{ 
                    py: 1.5,
                    backgroundColor: selectedStop && selectedStop.name === stop.name ? 
                      `${stopColors[stop.type]}22` : 'inherit',
                    userSelect: 'text',
                    cursor: 'pointer'
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{ 
                        bgcolor: stopColors[stop.type],
                        color: 'white',
                        width: 36,
                        height: 36
                      }}
                    >
                      {getStopIcon(stop.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" fontWeight="medium" sx={{ userSelect: 'text' }}>
                        {index + 1}. {stop.name}
                      </Typography>
                    }
                    secondary={
                      <div style={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)', userSelect: 'text' }}>
                        {/* Client info section */}
                        {stop.client && (
                          <div style={{ marginBottom: '4px' }}>
                            <Chip 
                              size="small"
                              variant="filled" 
                              color="primary" 
                              label={`${stop.client}${stop.client_type ? ` (${stop.client_type})` : ''}`}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </div>
                        )}
                        
                        {/* Address info */}
                        {stop.address && (
                          <div style={{ marginBottom: '4px', userSelect: 'text' }}>
                            {stop.address}
                          </div>
                        )}
                        
                        {/* Coordinates */}
                        <div style={{ marginBottom: '4px', userSelect: 'text', fontSize: '0.7rem' }}>
                          {stop.latitude}, {stop.longitude}
                        </div>
                        
                        {/* Distance info */}
                        {stop.distance_from_previous && (
                          <div style={{ marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            <Tooltip title="Distance from previous stop">
                              <Chip 
                                label={`Distance: ${stop.distance_from_previous} km`} 
                                size="small" 
                                variant="outlined"
                                color={getStopTypeColor(stop.type)}
                              />
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    }
                  />
                </ListItem>
                {index < trip.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>
      
      {/* Legend */}
      <Box sx={{ 
        display: 'flex', 
        mt: 2, 
        gap: 2, 
        flexWrap: 'wrap', 
        p: 2, 
        borderRadius: '8px', 
        bgcolor: '#f5f5f5' 
      }}>
        <Chip 
          icon={<Box 
            component="span" 
            sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              bgcolor: stopColors.start, 
              display: 'inline-block' 
            }} 
          />} 
          label="Start Location" 
          variant="outlined" 
          size="small" 
        />
              
        <Chip 
          icon={<Box 
            component="span" 
            sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              bgcolor: stopColors.intermediate, 
              display: 'inline-block' 
            }} 
          />} 
          label="Intermediate Stop" 
          variant="outlined" 
          size="small" 
        />
              
        <Chip 
          icon={<Box 
            component="span" 
            sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              bgcolor: stopColors.end, 
              display: 'inline-block' 
            }} 
          />} 
          label="End Location" 
          variant="outlined" 
          size="small" 
        />
              
        <Typography variant="caption" sx={{ ml: 'auto' }}>
          Click on any stop in the list to see details
        </Typography>
      </Box>
    </Box>
  );
};

export default TripMap;